import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  ComposedChart,
} from 'recharts';
import { History, Trash2, TrendingUp, TrendingDown, Minus, Scale, Activity, Loader2, Cloud } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DashboardHistory = ({ refreshTrigger }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  const loadHistory = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/bmi/history`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      }
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory, refreshTrigger]);

  const handleDeleteRecord = async (recordId) => {
    setDeleting(recordId);
    try {
      const response = await fetch(`${API_URL}/api/bmi/record/${recordId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        setHistory(prev => prev.filter(r => r.record_id !== recordId));
        toast.success('Record deleted');
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete record');
    } finally {
      setDeleting(null);
    }
  };

  const handleClearHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/bmi/history`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        setHistory([]);
        toast.success('History cleared');
      } else {
        throw new Error('Failed to clear');
      }
    } catch (error) {
      toast.error('Failed to clear history');
    }
  };

  const getCategoryBadgeClass = (category) => {
    if (category?.includes('Underweight')) return 'badge-underweight';
    if (category?.includes('Normal')) return 'badge-normal';
    if (category?.includes('Overweight')) return 'badge-overweight';
    if (category?.includes('Obese')) return 'badge-obese';
    return '';
  };

  // Calculate statistics
  const statistics = history.length > 0 ? {
    avgBmi: (history.reduce((sum, r) => sum + r.bmi, 0) / history.length).toFixed(1),
    avgWeight: (history.reduce((sum, r) => sum + r.weight, 0) / history.length).toFixed(1),
    minBmi: Math.min(...history.map(r => r.bmi)).toFixed(1),
    maxBmi: Math.max(...history.map(r => r.bmi)).toFixed(1),
    totalRecords: history.length
  } : null;

  // Prepare chart data
  const chartData = history.slice(0, 10).reverse().map(record => ({
    date: new Date(record.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    bmi: record.bmi,
    weight: record.weight
  }));

  if (loading) {
    return (
      <Card className="card-elevated">
        <CardContent className="py-12">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading your history...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (history.length === 0) {
    return (
      <Card className="card-elevated">
        <CardContent className="py-12">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Cloud className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">No History Yet</h3>
            <p className="text-muted-foreground text-sm max-w-sm mx-auto">
              Calculate your BMI and save it to start tracking your progress in the cloud.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Overview */}
      {statistics && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="card-elevated">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground mb-1">Avg BMI</p>
              <p className="text-2xl font-bold text-foreground">{statistics.avgBmi}</p>
            </CardContent>
          </Card>
          <Card className="card-elevated">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground mb-1">Avg Weight</p>
              <p className="text-2xl font-bold text-foreground">{statistics.avgWeight}</p>
              <p className="text-xs text-muted-foreground mt-1">kg</p>
            </CardContent>
          </Card>
          <Card className="card-elevated">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground mb-1">BMI Range</p>
              <p className="text-2xl font-bold text-foreground">
                {statistics.minBmi}-{statistics.maxBmi}
              </p>
            </CardContent>
          </Card>
          <Card className="card-elevated">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground mb-1">Records</p>
              <p className="text-2xl font-bold text-foreground">{statistics.totalRecords}</p>
              <p className="text-xs text-muted-foreground mt-1">saved</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts */}
      {chartData.length > 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* BMI Trend */}
          <Card className="card-elevated">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="w-4 h-4 text-primary" />
                BMI Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData}>
                    <defs>
                      <linearGradient id="bmiGradientDash" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis domain={['auto', 'auto']} tick={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                        fontSize: '12px'
                      }}
                    />
                    <Area type="monotone" dataKey="bmi" stroke="hsl(var(--primary))" fill="url(#bmiGradientDash)" />
                    <Line type="monotone" dataKey="bmi" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ fill: 'hsl(var(--primary))' }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Weight Trend */}
          <Card className="card-elevated">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Scale className="w-4 h-4 text-accent" />
                Weight Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis domain={['auto', 'auto']} tick={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                        fontSize: '12px'
                      }}
                      formatter={(value) => [`${value.toFixed(1)} kg`, 'Weight']}
                    />
                    <Bar dataKey="weight" fill="hsl(var(--accent))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* History Table */}
      <Card className="card-elevated">
        <CardHeader className="flex flex-row items-center justify-between pb-4 flex-wrap gap-2">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              Calculation History
            </CardTitle>
            <CardDescription className="hidden sm:block">Your cloud-synced BMI records</CardDescription>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                <Trash2 className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Clear All</span>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear all history?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete all your saved BMI records from the cloud. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleClearHistory} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Clear All
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardHeader>
        <CardContent className="p-0 sm:p-6 sm:pt-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Date</TableHead>
                  <TableHead>BMI</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="hidden sm:table-cell">Weight</TableHead>
                  <TableHead className="hidden md:table-cell">Mode</TableHead>
                  <TableHead className="w-[40px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.slice(0, 10).map((record) => (
                  <TableRow key={record.record_id}>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(record.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      })}
                    </TableCell>
                    <TableCell className="font-semibold">{record.bmi.toFixed(1)}</TableCell>
                    <TableCell>
                      <Badge className={`${getCategoryBadgeClass(record.who_category)} text-xs`}>
                        {record.who_category}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm hidden sm:table-cell">{record.weight.toFixed(1)} kg</TableCell>
                    <TableCell className="hidden md:table-cell">
                      {record.athlete_mode && (
                        <Badge variant="outline" className="text-xs">
                          <Activity className="w-3 h-3 mr-1" />
                          Athlete
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteRecord(record.record_id)}
                        disabled={deleting === record.record_id}
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      >
                        {deleting === record.record_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardHistory;
